import config from '../../config';
import { getFableticsEntitlement, userCanAccessGlobalPrivileges } from './user';
import { getRedirectUrl, shouldNavigateToRedirectUrl } from './activateUtr';

const globalPrivilegesUrl = `${config.apiHost}/v1/saml/globalprivileges`;

const HOME_ROUTE = '/home';

export const PostLoginRedirectMode = {
    SPA: 'spa',
    HARD: 'hard',
    SELF_TAB: 'selfTab',
};

const TPR_HANDLERS = {
    fl: (user) => getFableticsEntitlement(user)?.ctaUrl || null,
    gp: (user) => (userCanAccessGlobalPrivileges(user) ? globalPrivilegesUrl : null),
};

// Returns the absolute URL to navigate to in the same tab, or null when the
// caller should fall through to the existing redirect chain
// (next / redirect / home). When `tpr` is recognized but the user doesn't
// qualify, returns '/home' per product spec — partner pages send users to
// "/login?tpr=…" expecting either the partner destination or a graceful
// landing in UTR.
export const resolveThirdPartyRedirect = (tpr, user) => {
    const handler = TPR_HANDLERS[tpr];
    if (!handler) {
        return null;
    }
    return handler(user) || HOME_ROUTE;
};

const SAFE_REDIRECT_HOSTS = [config.appHost, config.nextAppHost, config.marketingAppHost, config.leaguesAppHost]
    .filter(Boolean)
    .map((value) => {
        try {
            return new URL(value).host;
        } catch {
            return null;
        }
    })
    .filter(Boolean);

// Guards a user-supplied redirect target against open-redirect abuse: returns the
// url only when it is a same-origin relative path or points at a known UTR host,
// otherwise null so the caller falls through to a safe default. Rejects '//host'
// and backslash tricks. Mirrors utr-web-app's safeRedirectUrl.
export const safeRedirectTarget = (url) => {
    // Non-strings reach here for real: query-string hands back an array for a repeated
    // param ("?next=a&next=b"), which scanners and some redirect-chain proxies produce.
    // The startsWith checks below sit outside the try/catch, so an unguarded array is an
    // unhandled TypeError that takes the whole page down instead of falling through.
    if (!url || typeof url !== 'string') {
        return null;
    }
    if (url.startsWith('/') && !url.startsWith('//') && !url.includes('\\')) {
        return url;
    }
    try {
        const parsed = new URL(url);
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
            return null;
        }
        return SAFE_REDIRECT_HOSTS.includes(parsed.host) ? url : null;
    } catch {
        return null;
    }
};

// Shared post-login destination decision so an MFA-required user (routed through
// the /mfa and /mfa-setup screens) lands where a normal login would. Priority
// mirrors the login page's original redirect chain. The raw query-param targets
// (next / upgradeAndRedirect / redirect) are open-redirect-guarded; the tpr and
// activation targets are already gated by their own flows.
export const resolvePostLoginRedirect = (parsed, activateUtr, user) => {
    if (shouldNavigateToRedirectUrl(activateUtr)) {
        return { mode: PostLoginRedirectMode.HARD, url: getRedirectUrl(activateUtr) };
    }
    const next = safeRedirectTarget(parsed?.next);
    if (next) {
        return { mode: PostLoginRedirectMode.SPA, url: next };
    }
    const upgradeAndRedirect = safeRedirectTarget(parsed?.upgradeAndRedirect);
    if (upgradeAndRedirect) {
        // UT-8135: prompt for Power upgrade, then redirect back to Insights.
        return { mode: PostLoginRedirectMode.SPA, url: upgradeAndRedirect };
    }
    const tprUrl = resolveThirdPartyRedirect(parsed?.tpr, user);
    if (tprUrl) {
        return { mode: PostLoginRedirectMode.HARD, url: tprUrl };
    }
    const redirect = safeRedirectTarget(parsed?.redirect);
    if (redirect) {
        return { mode: PostLoginRedirectMode.SELF_TAB, url: redirect };
    }
    return { mode: PostLoginRedirectMode.SPA, url: HOME_ROUTE };
};

// Partner / SAML (activation, tpr) destinations use a hard same-tab navigation:
// we're outside a click gesture, so window.open(..., '_blank') would be
// popup-blocked. A raw `redirect` keeps its historical window.open(..., '_self');
// in-app targets stay on the SPA history stack.
export const applyPostLoginRedirect = (redirect, history) => {
    if (!redirect) {
        return;
    }
    const { mode, url } = redirect;
    if (mode === PostLoginRedirectMode.HARD) {
        window.location.href = url;
    } else if (mode === PostLoginRedirectMode.SELF_TAB) {
        window.open(url, '_self');
    } else {
        history.replace(url);
    }
};
